import React, { useCallback, useLayoutEffect, useState } from 'react'

import type { SnackbarPayload } from 'components/Snackbar/useSnackbar'

import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'
import { useBookingDetail } from '../../hooks/useBookingDetail'
import { useBookingMasterData } from '../../hooks/useBookingMasterData'
import { useEditBooking } from '../../hooks/useEditBooking'
import type { Berth } from '../../types'
import { Dialog } from '../Dialog'
import { StatusChip } from '../StatusChip'

import { ConfirmControllerModal } from './ConfirmControllerModal'
import { ConfirmDriverModal } from './ConfirmDriverModal'
import { EditBookingForm } from './EditBookingForm'
import styles from './index.module.css'

type Props = {
  bookingId: string | null
  berths: Berth[]
  tenantId: string | null
  warehouseId: string | null
  isUnassigned: boolean
  onClose: () => void
  onNotify: (payload: SnackbarPayload) => void
}

export const EditBookingModal: React.FC<Props> = ({
  bookingId,
  berths,
  tenantId,
  warehouseId,
  isUnassigned,
  onClose,
  onNotify,
}) => {
  const [isClosing, setIsClosing] = useState(false)
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false)

  useLayoutEffect(() => {
    if (bookingId) {
      setIsClosing(false)
      setIsConfirmingDiscard(false)
    }
  }, [bookingId])

  const startClosing = useCallback(() => {
    setIsClosing(true)
  }, [])

  const handleAnimationEnd = useCallback(
    (e: React.AnimationEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return
      if (isClosing) {
        setIsClosing(false)
        onClose()
      }
    },
    [isClosing, onClose]
  )

  const { data: bookingDetailData, isLoading: isBookingDetailLoading } = useBookingDetail(bookingId)
  const booking = bookingDetailData?.data ?? null

  const {
    form,
    apiError,
    isSubmittingBasic,
    isSubmittingAdminNote,
    isSubmittingEntryInstruction,
    isBasicDirty,
    isAdminNoteDirty,
    isEntryInstructionDirty,
    isFormDirty,
    isFinish,
    handleSubmitBasic,
    handleSubmitAdminNote,
    handleSubmitEntryInstruction,
    masterIntegration,
  } = useEditBooking({
    booking,
    tenantId,
    isUnassigned,
    berths,
    onNotify,
    onClose: startClosing,
  })

  const { masterData } = useBookingMasterData(tenantId)

  const handleRequestClose = useCallback(() => {
    if (isFormDirty) {
      setIsConfirmingDiscard(true)
      return
    }
    startClosing()
  }, [isFormDirty, startClosing])

  const handleDiscardCancel = useCallback(() => {
    setIsConfirmingDiscard(false)
  }, [])

  const handleDiscardConfirm = useCallback(() => {
    setIsConfirmingDiscard(false)
    startClosing()
  }, [startClosing])

  useBodyScrollLock(bookingId !== null)

  if (!bookingId) return null

  const repeatLabel = booking?.repeatNew?.daily
    ? '日次'
    : booking?.repeatNew?.weekly
      ? '週次'
      : 'なし'

  const isLoadingInitial = isBookingDetailLoading && !booking

  return (
    <div className={`${styles.overlay} ${isClosing ? styles.overlayClosing : ''}`}>
      <div
        className={`${styles.content} ${isClosing ? styles.contentClosing : ''}`}
        onAnimationEnd={handleAnimationEnd}
      >
        <div className={styles.header}>
          <h3 className={styles.title}>予約編集</h3>
          <button
            className={styles.closeIconButton}
            onClick={handleRequestClose}
            aria-label="閉じる"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        <div className={styles.scrollArea}>
          {isLoadingInitial ? (
            <div className={styles.loadingArea} aria-busy="true" aria-label="読み込み中">
              <div className={styles.spinner} />
            </div>
          ) : (
            <>
              <div className={styles.summary}>
                <div className={styles.summaryItem}>
                  <span className={styles.summaryLabel}>予約番号</span>
                  <span className={styles.summaryValue}>{booking?.bookingNo || '-'}</span>
                </div>
                {booking?.status != null && <StatusChip status={booking.status} />}
              </div>

              {apiError && <p className={styles.apiError}>{apiError}</p>}
              <EditBookingForm
                form={form}
                booking={booking ?? null}
                berths={berths}
                tenantId={tenantId}
                warehouseId={warehouseId}
                isUnassigned={isUnassigned}
                isSubmittingBasic={isSubmittingBasic}
                isSubmittingAdminNote={isSubmittingAdminNote}
                isSubmittingEntryInstruction={isSubmittingEntryInstruction}
                isBasicDirty={isBasicDirty}
                isAdminNoteDirty={isAdminNoteDirty}
                isEntryInstructionDirty={isEntryInstructionDirty}
                isFinish={isFinish}
                repeatLabel={repeatLabel}
                masterData={masterData ?? null}
                masterIntegration={masterIntegration}
                onSubmitBasic={handleSubmitBasic}
                onSubmitAdminNote={handleSubmitAdminNote}
                onSubmitEntryInstruction={handleSubmitEntryInstruction}
                onNotify={onNotify}
              />
            </>
          )}
        </div>
      </div>

      {isConfirmingDiscard && (
        <Dialog
          onClose={handleDiscardCancel}
          prevButtonProps={{
            label: '編集を続ける',
            onClick: handleDiscardCancel,
            variant: 'cancel',
          }}
          nextButtonProps={{
            label: '破棄して閉じる',
            onClick: handleDiscardConfirm,
            variant: 'danger',
          }}
        >
          <p className={styles.dialogMessage}>
            保存していない変更があります。
            <br />
            編集内容を破棄しますか？
          </p>
        </Dialog>
      )}

      {masterIntegration.pendingDriverData && (
        <ConfirmDriverModal
          driverData={masterIntegration.pendingDriverData}
          onConfirm={masterIntegration.onConfirmDriver}
          onCancel={masterIntegration.onCancelDriver}
        />
      )}

      {masterIntegration.pendingControllerData && (
        <ConfirmControllerModal
          controllerData={masterIntegration.pendingControllerData}
          onConfirm={masterIntegration.onConfirmController}
          onCancel={masterIntegration.onCancelController}
        />
      )}
    </div>
  )
}
